/**
 * Проверка фотографии в браузере, до отправки.
 *
 * Дублирует по смыслу серверный preflight (supabase/functions/_shared/uploads),
 * но не по коду: клиенту запрещено импортировать серверные модули, а сервер
 * проверяет файл заново — по сигнатуре байтов, а не по заявленному типу.
 * Здесь задача другая: не заставлять человека ждать загрузки, чтобы узнать
 * про формат или размер.
 */

export interface PhotoLimits {
  maxBytes: number;
  minWidth: number;
  minHeight: number;
  allowedTypes: string[];
}

export const CLIENT_PHOTO_LIMITS: PhotoLimits = {
  maxBytes: 15 * 1024 * 1024,
  minWidth: 512,
  minHeight: 512,
  allowedTypes: ['image/jpeg', 'image/png', 'image/webp'],
};

export interface PhotoIssue {
  code: 'EMPTY_FILE' | 'UNSUPPORTED_FORMAT' | 'FILE_TOO_LARGE' | 'TOO_SMALL' | 'UNREADABLE';
  /** Что случилось и что делать — без кода ошибки и без технических деталей. */
  message: string;
}

export interface PhotoSize {
  width: number;
  height: number;
}

export interface PhotoCheckDeps {
  /** Возвращает размеры снимка или null, если файл не разбирается как изображение. */
  readSize: (file: File) => Promise<PhotoSize | null>;
}

export async function checkPhoto(
  file: File,
  deps: PhotoCheckDeps,
  limits: PhotoLimits = CLIENT_PHOTO_LIMITS,
): Promise<PhotoIssue | null> {
  if (file.size === 0) {
    return { code: 'EMPTY_FILE', message: 'Файл пустой. Выберите другую фотографию.' };
  }

  // HEIC провайдер картинок не принимает: без отказа здесь человек потратил бы
  // попытку и получил ошибку генерации. Айфон при выборе «image/*» обычно сам
  // отдаёт JPEG; если пришёл именно HEIC — объясняем, что сделать.
  if (isHeic(file)) {
    return {
      code: 'UNSUPPORTED_FORMAT',
      message: 'Формат HEIC не поддерживается. Сохраните снимок как JPEG или сделайте его скриншот и добавьте снова.',
    };
  }

  if (!limits.allowedTypes.includes(file.type)) {
    return {
      code: 'UNSUPPORTED_FORMAT',
      message: 'Такой формат не поддерживается. Подойдут JPEG, PNG или WebP.',
    };
  }

  if (file.size > limits.maxBytes) {
    const limitMb = Math.round(limits.maxBytes / 1024 / 1024);
    return {
      code: 'FILE_TOO_LARGE',
      message: `Файл больше ${limitMb} МБ. Уменьшите фотографию и попробуйте снова.`,
    };
  }

  const size = await deps.readSize(file);

  if (!size) {
    return { code: 'UNREADABLE', message: 'Не удалось открыть изображение. Попробуйте другой файл.' };
  }

  if (size.width < limits.minWidth || size.height < limits.minHeight) {
    return {
      code: 'TOO_SMALL',
      message: `Фотография ${size.width}×${size.height} — слишком мелкая. Нужен снимок от ${limits.minWidth}×${limits.minHeight}.`,
    };
  }

  return null;
}

/** Тип HEIC браузер часто не проставляет, поэтому проверяется ещё и расширение. */
export function isHeic(file: File): boolean {
  return /heic|heif/i.test(file.type) || /\.hei[cf]$/i.test(file.name);
}

/** Реальное измерение через <img>: в тестах подменяется. */
export function readSizeInBrowser(file: File): Promise<PhotoSize | null> {
  return new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const image = new Image();

    image.onload = () => {
      resolve({ width: image.naturalWidth, height: image.naturalHeight });
      URL.revokeObjectURL(url);
    };
    image.onerror = () => {
      resolve(null);
      URL.revokeObjectURL(url);
    };

    image.src = url;
  });
}
